import { BottomBar, BottomBarActions } from '@monorepo/base/src/components/bottom-bar/bottom-bar';
import { Page, Pagebar, PagebarTitle } from '@monorepo/base/src/components/page/page';
import { observer } from 'mobx-react';
import { ErrorMsg } from '../../../modules/components/error-msg/error-msg';
import { PrimaryButton } from '@monorepo/base/src/components/buttons/buttons';
import { ActionModal, ModalTypes } from '@monorepo/controlled/src/components/modal/modals/action-modal/action-modal';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { DispatchLogsTypes, useLogs } from '@monorepo/controlled/src/hooks/use-logs';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { IAdminxStore } from '../../../modules/stores';
import styles from './add-segmentations-to-campaign.module.scss';
import { Card } from '@monorepo/base/src/components/card/card';
import { SelectOptions } from '@monorepo/base/src/components/select-new/select-new';
import { id } from '@monorepo/tools/src/lib/types/primitives';
import { DropdownNew } from '@monorepo/base/src/components/dropdown-new/dropdown-new';
import { Icon } from '@monorepo/base/src/components/icon/icon';
import { BarLoader } from '@monorepo/base/src/components/bar-loader/bar-loader';
import { useList } from '../../../modules/hooks/tools/use-list';
import { WebappsCidModel } from '../../../modules/models/webapps-cid.model';
import { useCampaignInputOptions } from '../../../modules/hooks/apis/use-campaign-input-options';
import { WebappsCidApi } from '../../../modules/apis/webapps-cid.api';
import { IAskError } from '@monorepo/tools/src/lib/tools/ask/ask';
import { ISegmentation } from '../../../modules/models/campaign-segmentation.model';

const toWebappsCidOptions = (cids: WebappsCidModel[]): SelectOptions[] =>
	cids.map(item => ({
		label: `${item.getCid()} (${item.getName() || 'Unknown'})`,
		value: item.getCid(),
	}));

const getCidLabel = (cids: WebappsCidModel[], cid?: id) => {
	if (!cid) {
		return '';
	}
	const match = cids.find(item => String(item.getCid()) === String(cid));
	return match ? `${match.getCid()} (${match.getName() || 'Unknown'})` : String(cid);
};

const getAskErrorMessage = (err: IAskError, fallback: string): string => {
	const message = err?.data?.message;
	if (typeof message === 'string') {
		return message;
	}
	if (message && typeof message === 'object' && 'message' in message && typeof message.message === 'string') {
		return message.message;
	}
	if (err?.response?.status) {
		return `${fallback} (${err.response.status})`;
	}
	return fallback;
};

export const ToolsAddSegmentationsToCampaign = observer(() => {
	useCampaignInputOptions(['webappsCidSegments']);

	const { campaignSegmentsStore, modalsStore, campaignStore, webappsCidStore } = useStores<IAdminxStore>();
	const campaignSegmentsCrudStore = campaignSegmentsStore.getCrud();
	const formStore = campaignSegmentsCrudStore.getFormStore();
	const isValidForm = formStore.getIsValid();
	const isSuccess = campaignSegmentsCrudStore.getIsSuccess();
	const error = campaignSegmentsCrudStore.getHttpError();
	const [isFormClicked, setFormClicked] = useState<boolean>(false);
	const [existingSegments, setExistingSegments] = useState<ISegmentation[]>([]);
	const [isLoadingExistingSegments, setIsLoadingExistingSegments] = useState(false);

	const webAppsCidSegmentPool: SelectOptions[] = useMemo(
		() =>
			campaignStore
				.getInputOptions()
				.getWebappsCidSegments()
				.map(segment => ({
					label: segment.auction_package_name || '',
					value: segment.auction_package_id || '',
				})),
		[campaignStore.getInputOptions().getWebappsCidSegments()]
	);

	useList<WebappsCidModel>(webappsCidStore.listStore);

	const webappsCidOptions = useMemo(
		() => toWebappsCidOptions(webappsCidStore.listStore.getList()),
		[webappsCidStore.listStore.getList()]
	);

	const selectedCid = campaignSegmentsCrudStore.getData().getCid();
	const segmentations = campaignSegmentsCrudStore.getData().getSegmentations();
	const existingAuctionPackageIds = useMemo(
		() =>
			new Set(
				existingSegments
					.map(segment => segment.auction_package_id)
					.filter(packageId => packageId != null && packageId !== '')
					.map(String)
			),
		[existingSegments]
	);
	const maxSegmentRows = Math.max(0, webAppsCidSegmentPool.length - existingAuctionPackageIds.size);
	const isCreateFormComplete =
		Boolean(selectedCid) &&
		(segmentations?.length ?? 0) > 0 &&
		segmentations!.every(segment => segment.getAuctionPackageId() && segment.getTargetCid());

	const { dispatchLog } = useLogs();

	const buildSegmentOptions = (rowAuctionPackageId?: id): SelectOptions[] => {
		const rowId = rowAuctionPackageId != null ? String(rowAuctionPackageId) : undefined;
		const usedInForm = new Set(
			(segmentations || [])
				.filter(seg => seg.getAuctionPackageId() != null && String(seg.getAuctionPackageId()) !== rowId)
				.map(seg => String(seg.getAuctionPackageId()))
		);

		return webAppsCidSegmentPool.map(seg => {
			const packageId = String(seg.value);
			return {
				...seg,
				disabled: existingAuctionPackageIds.has(packageId) || (usedInForm.has(packageId) && rowId !== packageId),
			};
		});
	};

	useEffect(() => {
		campaignSegmentsCrudStore.reset();
	}, []);

	useEffect(() => {
		if (!isValidForm && isFormClicked) {
			dispatchLog({
				msg: 'Please complete all mandatory fields',
				type: DispatchLogsTypes.Error,
			});
		}
	}, [isFormClicked]);

	useEffect(() => {
		if (isSuccess) {
			dispatchLog({
				msg: `Segmentation was created successfully`,
				type: DispatchLogsTypes.Success,
			});
			campaignSegmentsCrudStore.setIsSuccess(false);
			campaignSegmentsCrudStore.reset();
			setExistingSegments([]);
		}
	}, [isSuccess]);

	const onSubmitCids = () => {
		setFormClicked(true);
		if (campaignSegmentsCrudStore.isValid()) {
			modalsStore.addModal({
				children: (
					<ActionModal
						type={ModalTypes.Warning}
						primaryBtnTitle={'Yes, I am sure'}
						onAction={() => {
							campaignSegmentsCrudStore.createSegmentation();
						}}>
						<Fragment>Caution: You are about to create a CID which might be active, are you sure?</Fragment>
					</ActionModal>
				),
			});
		}
	};

	const onSelectCid = (cidValues: id[]) => {
		const newCid = cidValues[0];
		campaignSegmentsCrudStore.getData().setCid(newCid);
		campaignSegmentsCrudStore.getData().setSegmentations([]);
		setExistingSegments([]);

		if (!newCid) {
			return;
		}

		setIsLoadingExistingSegments(true);
		WebappsCidApi.getSegments(newCid)
			.then(res => {
				const segments = res.cid_segments || [];
				setExistingSegments(segments);
				const existingCount = new Set(
					segments
						.map(segment => segment.auction_package_id)
						.filter(packageId => packageId != null && packageId !== '')
						.map(String)
				).size;
				if (webAppsCidSegmentPool.length - existingCount > 0) {
					campaignSegmentsCrudStore.getData().addSegmentation();
				}
			})
			.catch((err: IAskError) => {
				dispatchLog({
					msg: getAskErrorMessage(err, "Couldn't load existing segments"),
					type: DispatchLogsTypes.Error,
				});
				setExistingSegments([]);
				campaignSegmentsCrudStore.getData().addSegmentation();
			})
			.finally(() => setIsLoadingExistingSegments(false));
	};

	const onSelectWebappsCidSegment = (webappsCid: id[], segmentId: string) => {
		const segment = segmentations?.find(seg => seg.getId().toString() === segmentId);
		segment?.setAuctionPackageId(webappsCid[0]);
		segment?.setAuctionPackageName(
			webAppsCidSegmentPool.find(seg => String(seg.value) === String(webappsCid[0]))?.label.toString() || segmentId
		);
	};

	const onSelectTargetCid = (targetCidValues: id[], segmentId: string) => {
		const segment = segmentations?.find(seg => seg.getId().toString() === segmentId);
		segment?.setTargetCid(targetCidValues[0]);
	};

	const renderExistingSegments = () => {
		if (!selectedCid || isLoadingExistingSegments || existingSegments.length === 0) {
			return null;
		}

		const cids = webappsCidStore.listStore.getList();

		return existingSegments.map((segment, index) => {
			const targetCid = segment.target_cid;
			const targetCidLabel = getCidLabel(cids, targetCid);

			return (
				<Card
					key={`existing-${segment.auction_package_id ?? index}`}
					title={'Existing Webapps CID Segment'}
					icon={'announcement-01'}>
					<DropdownNew
						label={'Select Segment'}
						classNames={{ dropdownWrapper: styles.dropdownWrapper, parentSelectWrapper: styles.parentSelectWrapper }}
						options={[
							{
								label: segment.auction_package_name || String(segment.auction_package_id),
								value: segment.auction_package_id || '',
							},
						]}
						defaultValues={segment.auction_package_id ? [segment.auction_package_id] : ([] as id[])}
						multi={false}
						disabled
					/>
					<DropdownNew
						label={'Target CID'}
						classNames={{ dropdownWrapper: styles.cidDropdownWrapper, parentSelectWrapper: styles.parentSelectWrapper }}
						options={[
							{
								label: targetCidLabel,
								value: targetCid || '',
							},
						]}
						defaultValues={targetCid ? [targetCid] : ([] as id[])}
						multi={false}
						disabled
					/>
				</Card>
			);
		});
	};

	const renderSegments = () => {
		if (!selectedCid) {
			return null;
		}

		return segmentations?.map((segment, index) => {
			return (
				<Card key={index} title={'Select Webapps CID Segment'} icon={'announcement-01'}>
					<Icon
						onClick={() => campaignSegmentsCrudStore.getData().removeSegmentation(segment.getId())}
						disabled={segmentations.length <= 1}
						isMFP
						className={styles.closeIcon}>
						x-close
					</Icon>
					<DropdownNew
						label={'Select Segment'}
						classNames={{ dropdownWrapper: styles.dropdownWrapper, parentSelectWrapper: styles.parentSelectWrapper }}
						options={buildSegmentOptions(segment.auction_package_id)}
						defaultValues={segment.auction_package_id ? [segment.auction_package_id] : ([] as id[])}
						autocomplete={true}
						autocompletePlaceholder={'Search CID Segment'}
						multi={false}
						required
						onSelect={val => onSelectWebappsCidSegment(val, segment.getId().toString())}
					/>
					<DropdownNew
						label={'Target CID'}
						classNames={{ dropdownWrapper: styles.cidDropdownWrapper, parentSelectWrapper: styles.parentSelectWrapper }}
						options={webappsCidOptions}
						defaultValues={segment.target_cid ? [segment.target_cid] : ([] as id[])}
						autocomplete={true}
						autocompletePlaceholder={'Search Target CID'}
						multi={false}
						required
						onSelect={val => onSelectTargetCid(val, segment.getId().toString())}
						disabled={webappsCidStore.listStore.getIsLoading()}
					/>
					{segmentations.length - 1 === index ? (
						<PrimaryButton
							disabled={!segment.getAuctionPackageId() || !segment.getTargetCid() || segmentations.length >= maxSegmentRows}
							className={`${styles.inputSize} ${styles.addSegment}`}
							onClick={() => campaignSegmentsCrudStore.getData().addSegmentation()}
							iconSize={'20px'}
							iconColor="rgb(99, 102, 241)"
							icon={'plus'}>
							Add Segment
						</PrimaryButton>
					) : null}
				</Card>
			);
		});
	};

	return (
		<Page>
			<div className={styles.formWrapper}>
				<div className={styles.pagebar}>
					<Pagebar>
						<PagebarTitle>Create Segmentation</PagebarTitle>
					</Pagebar>
				</div>

				<div className={styles.segmentation}>
					<Card title={'Select CID'} icon={'announcement-01'}>
						<DropdownNew
							classNames={{ dropdownWrapper: styles.cidDropdownWrapper, parentSelectWrapper: styles.parentSelectWrapper }}
							options={webappsCidOptions}
							defaultValues={selectedCid ? [selectedCid] : ([] as id[])}
							autocomplete={true}
							autocompletePlaceholder={'Search CID'}
							required
							onSelect={onSelectCid}
							disabled={webappsCidStore.listStore.getIsLoading() || isLoadingExistingSegments}
						/>
						<div className={styles.limitLoaderSize}>
							<BarLoader is={webappsCidStore.listStore.getIsLoading() || isLoadingExistingSegments} />
						</div>
					</Card>
					{renderExistingSegments()}
					{renderSegments()}
				</div>
			</div>
			<BottomBar>
				{error ? <ErrorMsg error={error} /> : null}
				<BottomBarActions>
					<PrimaryButton disabled={!isCreateFormComplete} onClick={() => onSubmitCids()}>
						Submit
					</PrimaryButton>
				</BottomBarActions>
			</BottomBar>
		</Page>
	);
});
